import { Component, inject, Input, OnChanges, SimpleChanges, booleanAttribute } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { EmptyStateComponent, TagComponent, TileComponent } from '@tecalisdev/infinity';
import { OperationOcrChecksAntifraudComponent } from '../../../../../shared/components/operations/ocr/operation-ocr-checks-antifraud/operation-ocr-checks-antifraud.component';
import { OperationOcrChecksCompareInfoComponent } from '../../../../../shared/components/operations/ocr/operation-ocr-checks-compare-info/operation-ocr-checks-compare-info.component';
import { OperationFormChecksCompareInfoComponent } from '../../../../../shared/components/operations/form/operation-form-checks-compare-info/operation-form-checks-compare-info.component';
import { OperationOcrChecksRequirementsComponent } from '../../../../../shared/components/operations/ocr/operation-ocr-checks-requirements/operation-ocr-checks-requirements.component';
import {
	CHECK,
	IOperationOcrDetail,
	IProcessDetailResponse,
	PROCESS_STATUS,
} from '../../../../../processes/models/processes.models';
import { ProcessHelperService } from '../../../../../processes/services/process-helper.service';
import { MasterService } from '../../../../../shared/services/master.service';
import { IOperationDetailResponse } from '../../../../models/operations.models';
import {
	isOcrKoMatchDataError,
	shouldSuppressOcrKoInAntifraud,
} from '../../../../utils/form-operation-match-compare.util';
import { RolePermissionsService } from '../../../../../core/services/role-permissions.service';

@Component({
	selector: 'app-operation-detail-tabs-checks-ocr',
	imports: [
		TranslatePipe,
		TileComponent,
		TagComponent,
		EmptyStateComponent,
		OperationOcrChecksRequirementsComponent,
		OperationOcrChecksCompareInfoComponent,
		OperationFormChecksCompareInfoComponent,
		OperationOcrChecksAntifraudComponent,
	],
	templateUrl: './operation-detail-tabs-checks-ocr.component.html',
	styleUrl: './operation-detail-tabs-checks-ocr.component.scss',
})
export class OperationDetailTabsChecksOcrComponent implements OnChanges {
	@Input() detail: IOperationDetailResponse;
	/** En contexto FORM con cotejo match_ocr, KO_OCR va en tile Contraste (FORM_MATCH_DATA). */
	@Input({ transform: booleanAttribute }) suppressKoOcrInAntifraud = false;
	/** Snapshot FORM para integrar `FORM_MATCH_DATA` en el mismo tile Contraste. */
	@Input() formMatchChecksDetail: IProcessDetailResponse | null = null;

	public ocr: IOperationOcrDetail | null = null;
	public checksDetail: IProcessDetailResponse | null = null;
	public hasRequirementsContent = false;
	public hasCompareContent = false;
	public hasAntifraudContent = false;
	public antifraudChecks: (CHECK | undefined)[] = [];
	public compareExtraChecks: CHECK[] = [];

	private processHelper = inject(ProcessHelperService);
	private readonly rolePermissions = inject(RolePermissionsService);
	public readonly masterService = inject(MasterService);

	public ngOnChanges(changes: SimpleChanges): void {
		if (!changes['detail'] && !changes['suppressKoOcrInAntifraud'] && !changes['formMatchChecksDetail']) {
			return;
		}
		this.recomputeFromDetail();
	}

	private recomputeFromDetail(): void {
		const ocr = this.resolveOcr(this.detail);
		this.ocr = ocr;

		if (ocr) {
			this.processHelper.setYearsOld(ocr);
			this.checksDetail = this.buildChecksDetail(ocr);
		} else {
			this.checksDetail = null;
		}

		this.hasRequirementsContent = this.computeHasRequirements(ocr);
		this.hasCompareContent = this.computeHasCompare(ocr);
		this.compareExtraChecks = this.buildCompareExtraChecks(ocr);
		this.antifraudChecks = this.buildAntifraudChecks(ocr);
		this.hasAntifraudContent = this.computeHasAntifraud(ocr, this.detail, this.antifraudChecks);
	}

	private resolveOcr(detail: IOperationDetailResponse | undefined): IOperationOcrDetail | null {
		if (!detail?.data) {
			return null;
		}
		return detail.data as unknown as IOperationOcrDetail;
	}

	private buildChecksDetail(ocr: IOperationOcrDetail): IProcessDetailResponse {
		return {
			general: {
				profile: { uuid: '', alias: '' },
				sms: [],
				auth: '',
				company: '',
				company_uuid: '',
				location: [],
				devices: [],
				init_operation: '',
				total_time: 0,
				end_operation: '',
				validation: '',
			},
			process_uuid: this.detail?.process_uuid ?? '',
			process_details: '',
			status_process: PROCESS_STATUS.OK,
			data: { OCR: ocr } as IProcessDetailResponse['data'],
			locks: this.detail?.locks,
		} as IProcessDetailResponse;
	}

	private isKoOcrSuppressedInAntifraud(ocr: IOperationOcrDetail | null): boolean {
		return this.suppressKoOcrInAntifraud || shouldSuppressOcrKoInAntifraud(ocr, null);
	}

	private buildCompareExtraChecks(ocr: IOperationOcrDetail | null): CHECK[] {
		if (!isOcrKoMatchDataError(ocr) || !this.isKoOcrSuppressedInAntifraud(ocr)) {
			return [];
		}
		// El tile FORM ya muestra `FORM_MATCH_DATA`; en OCR puro el KO va a Contraste.
		if (this.suppressKoOcrInAntifraud) {
			return [];
		}
		return [CHECK.KO_OCR];
	}

	private buildAntifraudChecks(ocr: IOperationOcrDetail | null): (CHECK | undefined)[] {
		const includeKoOcr =
			this.processHelper.shouldShowOperationKoReason(ocr) && !this.isKoOcrSuppressedInAntifraud(ocr);

		return [
			includeKoOcr ? CHECK.KO_OCR : undefined,
			CHECK.PROHIBITED_CAMERAS_OCR,
			CHECK.PHOTOCOPY,
			CHECK.SCREENPHOTO,
			CHECK.MANIPULATED_PHOTO,
			CHECK.FRAUD_ID_BLOCK,
		];
	}

	private computeHasRequirements(ocr: IOperationOcrDetail | null): boolean {
		const a = ocr?.antifraud;
		return !!(a?.DOCUMENT_TYPE || a?.COUNTRY || a?.EXPIRATED || a?.LEGAL_AGE);
	}

	private computeHasCompare(ocr: IOperationOcrDetail | null): boolean {
		return (
			this.processHelper.hasDisplayableOcrCompareAntifraud(ocr) ||
			this.buildCompareExtraChecks(ocr).length > 0 ||
			!!this.formMatchChecksDetail
		);
	}

	public hasOcrCompareChecks(ocr: IOperationOcrDetail | null): boolean {
		return (
			this.processHelper.hasDisplayableOcrCompareAntifraud(ocr) || this.buildCompareExtraChecks(ocr).length > 0
		);
	}

	private computeHasAntifraud(
		ocr: IOperationOcrDetail | null,
		detail: IOperationDetailResponse | undefined,
		checks: (CHECK | undefined)[],
	): boolean {
		const a = ocr?.antifraud;
		const hasKoOcr = checks.includes(CHECK.KO_OCR);
		return !!(
			a?.PROHIBITED_CAMERAS ||
			a?.PHOTOCOPY ||
			a?.MANIPULATED_PHOTO ||
			a?.SCREENPHOTO ||
			(detail?.locks?.blocked_identifiers?.length ?? 0) > 0 ||
			(hasKoOcr && ocr?.actions?.KO)
		);
	}
}
